import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
    CreateSurveyButton,
    SecondaryButton,
    TertiaryButton,
} from '@/features/survey-builder/Tabs/CustomButton'
import { CircleCheck, X, Check } from 'lucide-react'
import { Eye, EyeOff } from 'lucide-react'
import { useMemo, useState, useEffect } from 'react'
import { useAppSelector } from '@/store'
import { useToast } from '@/hooks/use-toast'
import { useNavigate } from 'react-router-dom'

import { DowngradeModal } from '@/components/DowngradeModal'
import { useMantle } from '@heymantle/react'
import { usePageTracking } from '@/hooks/usePageTracking'
import { useStoreData } from '@/hooks/useStoreData'
import { FEATURE_IDS } from '@/config/features'

// Type and constant definitions
interface PlanFeature {
    id: string
    name: string
    type: 'boolean' | 'limit' | 'limit_with_overage'
    description?: string
    value: boolean | number
    defaultValue?: boolean | number
    displayOrder: number
    visible?: boolean
}

interface Plan {
    id: string
    name: string
    amount: number
    description?: string | null
    currencyCode: string
    presentmentAmount: number
    presentmentCurrencyCode: string | number
    monthlyAmount?: number
    public: boolean
    visible: boolean
    trialDays: number
    interval: 'EVERY_30_DAYS' | 'ANNUAL'
    features: Record<string, PlanFeature>
    featuresOrder: string[]
    total: number
    subtotal: number
    eligible: boolean
    type: string
    flexBilling?: boolean
    isCurrentPlan?: boolean
    availability?:
    | 'hidden'
    | 'public'
    | 'customerTag'
    | 'customer'
    | 'shopifyPlan'
}

const POPULAR = Number(import.meta.env.VITE_POPULAR_PLAN_AMOUNT)
const FREE_PLAN_AMOUNT = 0
const UNLIMITED_LABEL = 'Unlimited'
const DEFAULT_MIN_WIDTH = 500
const DEFAULT_MAX_WIDTH = 1000
const DEFAULT_CARD_WIDTH = 400

function formatPrice(amount: number | undefined | null, currency = 'USD') {
    if (amount == null) return '$0'
    return amount.toLocaleString('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

function getSortedFeatures(plan: Plan | null) {
    if (!plan) return []
    return Object.entries(plan.features)
        .filter(([_, feature]) => (feature as PlanFeature).visible !== false)
        .map(([_, feature]) => feature as PlanFeature)
        .sort((a, b) => a.displayOrder - b.displayOrder)
}

const PlansCard = ({
    id,
    name,
    amount,
    interval,
    features,
    isCurrentPlan,
    onSubscribe,
    onClick,
    selected,
}: Plan & {
    onSubscribe: (planId: string, interval: string) => Promise<void>
    onClick: () => void
    selected: boolean
}) => {
    return (
        <Card
            className="!px-0 border-[#EAECF0] shadow-none"
            onClick={onClick}
            tabIndex={0}
            aria-pressed={selected}
            role="button"
        >
            <CardHeader className="px-4 py-4 flex flex-col items-start">
                <div className="flex items-center justify-between gap-2 w-full">
                    <CardTitle className="text-[14px] font-[650] flex items-center gap-2">
                        {name}{' '}
                        {isCurrentPlan ? (
                            <Badge
                                variant="secondary"
                                className="bg-[#E0EFFF] py-[2px] px-[10px] font-[650] text-[#1D5EFF] hover:bg-[#B3D1FF] rounded-xl"
                            >
                                Current Plan
                            </Badge>
                        ) : (
                            amount === POPULAR && (
                                <Badge
                                    variant="secondary"
                                    className="bg-[#E0F0FF] py-[2px] px-[10px] font-[650] text-[#00527C] hover:bg-[#CDFEE1] rounded-xl"
                                >
                                    Popular
                                </Badge>
                            )
                        )}
                    </CardTitle>
                    <div>
                        {selected ? (
                            <div className="w-5 h-5 border border-[#464646] bg-[#464646] rounded-full flex items-center justify-center">
                                <Check
                                    size={14}
                                    className="text-[#fff]"
                                    stroke={'white'}
                                />
                            </div>
                        ) : (
                            <div className="w-5 h-5 border border-[#464646] rounded-full" />
                        )}
                    </div>
                </div>
                <CardTitle className="gap-1 text-[13px] font-[450]">
                    <div>
                        <span className="text-[20px] font-[650]">
                            {formatPrice(amount)}
                        </span>{' '}
                        / {interval === 'EVERY_30_DAYS' ? 'month' : 'year'}
                    </div>
                    <div className="flex items-end gap-1">
                        <span
                            key={FEATURE_IDS.SURVEY_RESPONSES}
                            className="text-[12px] text-[#616161] font-normal"
                        >
                            <span>
                                {features[FEATURE_IDS.SURVEY_RESPONSES]
                                    ?.type === 'limit' && (
                                        <span>
                                            {typeof features[
                                                FEATURE_IDS.SURVEY_RESPONSES
                                            ]?.value === 'number' &&
                                                (features[FEATURE_IDS.SURVEY_RESPONSES]
                                                    ?.value as number) >= 0 ? (
                                                <span>
                                                    Upto{' '}
                                                    {
                                                        features[
                                                            FEATURE_IDS
                                                                .SURVEY_RESPONSES
                                                        ]?.value
                                                    }
                                                </span>
                                            ) : (
                                                UNLIMITED_LABEL
                                            )}
                                        </span>
                                    )}{' '}
                                <span>
                                    {
                                        features[FEATURE_IDS.SURVEY_RESPONSES]
                                            ?.name
                                    }
                                </span>
                            </span>
                        </span>
                    </div>
                </CardTitle>
            </CardHeader>
        </Card>
    )
}

const HiddenPlans = () => {
    usePageTracking('Hidden Plans', { page_type: 'exclusive_plans' })

    const [billingTab, setBillingTab] = useState<'monthly' | 'yearly'>(
        'monthly'
    )
    const { plans, subscription, subscribe } = useMantle()
    const [showDowngradeModal, setShowDowngradeModal] = useState(false)
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
    const [isDowngrading, setIsDowngrading] = useState(false)
    const [isSubscribing, setIsSubscribing] = useState(false)
    const { merchantId, storeId } = useAppSelector(
        (state) => state.auth.context
    )
    const { data: storeData } = useStoreData()
    const storeUrl = storeData?.store?.storeUrl || ''
    const { toast } = useToast()
    const navigate = useNavigate()

    // Password protection state
    const [password, setPassword] = useState('')
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [error, setError] = useState('')
    const [showPassword, setShowPassword] = useState(false)

    // Password logic: use env password
    const EXCLUSIVE_PLANS_PASSWORD =
        import.meta.env.VITE_EXCLUSIVE_PLANS_PASSWORD || 'EXCLUSIVE100'

    useEffect(() => {
        // Check sessionStorage for password
        const sessionPass = sessionStorage.getItem('hiddenPlansAuth')
        if (sessionPass === EXCLUSIVE_PLANS_PASSWORD) {
            setIsAuthenticated(false)
        }
    }, [storeUrl])

    const handlePasswordSubmit = (e?: React.FormEvent) => {
        e?.preventDefault()
        if (password === EXCLUSIVE_PLANS_PASSWORD) {
            setIsAuthenticated(true)
            sessionStorage.setItem('hiddenPlansAuth', password)
            setError('')
        } else {
            setError('Incorrect password')
        }
    }

    // Set default selected plan to current or first available
    useEffect(() => {
        if (plans.length > 0) {
            const hiddenPlans = plans.filter(
                (plan) => plan.availability === 'customerTag'
            )
            const current = hiddenPlans.find(
                (plan) => plan.id === subscription?.plan.id
            )
            setSelectedPlan(current || hiddenPlans[0] || null)
        }
    }, [plans, subscription])

    // Memoize filtered plans for performance
    const allPlans = useMemo(
        () => plans.filter((plan) => plan.availability === 'customerTag'),
        [plans]
    )

    const sortedFeatures = useMemo(
        () =>
            getSortedFeatures(selectedPlan).filter(
                (feature) => !(feature.type === 'limit' && feature.value === 0)
            ),
        [selectedPlan]
    )

    // Handles plan subscription
    const handleSubscribe = async (planId: string) => {
        if (!merchantId || !storeId) return
        const currentPlan = plans?.find(
            (plan) => plan.id === subscription?.plan.id
        )
        const newPlan = plans?.find((plan) => plan.id === planId)
        // Show downgrade modal if switching from paid to free plan
        if (currentPlan && currentPlan.amount > 0 && newPlan?.amount === 0) {
            setSelectedPlan(newPlan as Plan)
            setShowDowngradeModal(true)
            return
        }
        try {
            setIsSubscribing(true)
            const newSubscription = await subscribe({
                planId,
                returnUrl: window.location.origin,
            })
            if (newSubscription?.confirmationUrl) {
                localStorage.setItem('selectedPlanUpgrade', 'true')
                window.location.href =
                    newSubscription.confirmationUrl.toString()
            }
            if (newPlan?.amount === 0) {
                navigate('/')
                window.location.reload()
            }
        } catch (error) {
            console.error(error)
            toast({
                title: 'Error',
                description: 'Failed to update subscription',
                variant: 'destructive',
            })
        } finally {
            setIsSubscribing(false)
        }
    }

    // Handles confirming a downgrade
    const handleDowngradeConfirm = async () => {
        if (!selectedPlan) return
        try {
            setIsDowngrading(true)
            const newSubscription = await subscribe({
                planId: selectedPlan.id,
                returnUrl: window.location.origin,
            })
            if (newSubscription?.confirmationUrl) {
                window.location.href =
                    newSubscription.confirmationUrl.toString()
            }
            navigate('/')
            window.location.reload()
        } catch (error) {
            console.error(error)
            toast({
                title: 'Error',
                description: 'Failed to downgrade subscription',
                variant: 'destructive',
            })
        } finally {
            setIsDowngrading(false)
            setShowDowngradeModal(false)
        }
    }

    if (!isAuthenticated) {
        return (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-100px)]">
                <form
                    onSubmit={handlePasswordSubmit}
                    className="flex flex-col gap-4 w-80 p-8 border rounded-lg shadow bg-white"
                >
                    <h2 className="text-xl font-bold text-center">
                        Exclusive Plans Access
                    </h2>
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="border px-3 py-2 rounded w-full pr-10"
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                            tabIndex={0}
                            aria-label={
                                showPassword ? 'Hide password' : 'Show password'
                            }
                        >
                            {showPassword ? (
                                <EyeOff size={20} />
                            ) : (
                                <Eye size={20} />
                            )}
                        </button>
                    </div>
                    {error && (
                        <span className="text-red-500 text-sm">{error}</span>
                    )}
                    <div className="flex justify-end">
                        <CreateSurveyButton
                            text="Continue"
                            onClick={() => handlePasswordSubmit()}
                        />
                    </div>
                </form>
            </div>
        )
    }

    return (
        <div className="h-full flex items-start justify-center">
            <DowngradeModal
                open={showDowngradeModal}
                onOpenChange={setShowDowngradeModal}
                onConfirm={handleDowngradeConfirm}
                isLoading={isDowngrading}
            />
            <div
                className={`w-full min-w-[${DEFAULT_MIN_WIDTH}px] max-w-[${DEFAULT_MAX_WIDTH}px] mb-40 flex flex-col items-center`}
            >
                <header className="w-[80%] py-6 flex flex-col gap-[10px]">
                    <h2 className="font-[650] text-[24px] text-center">
                        🎉 Surprise! You’ve discovered an exclusive plans page.
                    </h2>
                    <p className="text-[14px] text-[#616161] font-[450] text-center">
                        These plans are not visible to everyone. Enjoy exploring
                        your exclusive options!
                    </p>
                </header>
                {allPlans.length === 0 ? (
                    <div className="text-center text-lg text-gray-500 mt-16">
                        Oops! Looks like you have no exclusive plans.
                    </div>
                ) : (
                    <Tabs
                        defaultValue={billingTab}
                        onValueChange={(value) =>
                            setBillingTab(value as 'monthly' | 'yearly')
                        }
                        className="w-full"
                    >
                        <TabsContent
                            value="monthly"
                            className="flex justify-center w-full gap-4"
                        >
                            <div className="flex w-[50%] min-w-[400px] max-w-[600px] flex-col gap-2">
                                {allPlans.map((plan) => (
                                    <PlansCard
                                        key={plan.id}
                                        {...plan}
                                        onSubscribe={handleSubscribe}
                                        isCurrentPlan={
                                            subscription?.plan.id === plan.id
                                        }
                                        onClick={() => setSelectedPlan(plan)}
                                        selected={selectedPlan?.id === plan.id}
                                    />
                                ))}
                                {selectedPlan && (
                                    <div className="w-[80%] flex justify-start gap-4 mt-4">
                                        {subscription?.plan?.id && (
                                            <SecondaryButton
                                                text="Back"
                                                onClick={() =>
                                                    navigate(
                                                        '/settings?tab=billing'
                                                    )
                                                }
                                                aria-label="Back to billing settings"
                                            />
                                        )}
                                        <TertiaryButton
                                            disabled={
                                                selectedPlan.id ===
                                                subscription?.plan.id ||
                                                isSubscribing
                                            }
                                            text={`Continue with ${selectedPlan.name} plan`}
                                            onClick={() =>
                                                handleSubscribe(selectedPlan.id)
                                            }
                                            aria-label={`Continue with ${selectedPlan.name} plan`}
                                        />
                                    </div>
                                )}
                            </div>
                            <div
                                className={`w-[${DEFAULT_CARD_WIDTH}px] min-w-[300px]`}
                            >
                                <div className="text-[20px] font-[650] text-[#303030] px-4 flex items-center gap-2">
                                    {selectedPlan?.amount === 0
                                        ? `Limited features included:`
                                        : 'All features included:'}
                                </div>
                                <CardContent className="px-4 pt-4">
                                    <ul className="list-none space-y-6 mt-2">
                                        {sortedFeatures.map((feature) => (
                                            <li
                                                key={feature.id}
                                                className="flex items-start gap-2 text-[16px] font-[550] text-[#464646]"
                                            >
                                                <span className="mt-0.5">
                                                    {(feature.type ===
                                                        'boolean' ||
                                                        feature.type ===
                                                        'limit') &&
                                                        feature.value ? (
                                                        <CircleCheck
                                                            size={20}
                                                            stroke={'#008060'}
                                                        />
                                                    ) : (
                                                        <X
                                                            size={16}
                                                            stroke={'#FF3300'}
                                                        />
                                                    )}
                                                </span>
                                                <span className="text-[16px] font-[550] text-[#464646]">
                                                    {feature.type ===
                                                        'limit' && (
                                                            <span>
                                                                {typeof feature.value ===
                                                                    'number' &&
                                                                    feature.value >=
                                                                    0 ? (
                                                                    <span>
                                                                        Upto{' '}
                                                                        {
                                                                            feature.value
                                                                        }
                                                                    </span>
                                                                ) : (
                                                                    UNLIMITED_LABEL
                                                                )}
                                                            </span>
                                                        )}{' '}
                                                    <span>{feature.name}</span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </div>
                        </TabsContent>
                    </Tabs>
                )}
            </div>
        </div>
    )
}

export default HiddenPlans
